/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { useBetween } from 'use-between';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../layout';
import Navbar from '../../components/navbar';
import AwareButton from '../../components/button';
import useApi from '../useApi';
import usePickupForm from './usePickupForm';
import usePickupData from './usePickupData';
import OrderInfoCard from './OrderInfoCard';
import PickupScheduleForm from './PickupScheduleForm';
import { CustomerInfoCard, ContactRepCard } from './CustomerInfoCard';
import './index.scss';
import { toast } from "react-toastify";
import { toDateType, toTwoDecimals } from '../../plugins';


/**
 * PaymentPickup Component
 * Main component for scheduling payment pickups
 * Optimized with custom hooks, memoization, and separated concerns
 */
const PaymentPickup = () => {
    const navigate = useNavigate();
    const params = useParams();
    const invoiceId = params.id;
    const [invoice, setInvoice] = useState({});
    const [order, setOrder] = useState({});
    const [isRescheduling, setIsRescheduling] = useState(false);

    // API methods from shared hook
    const {
        customerId,
        addPickUpPayment,
        reschedulePaymentPickup,
        getDelivery,
        getOrder,
        logout,
        setCartOpen,
        updating,
        customer,
        getOpenInvoiceByInvoiceId,
        setUpdating,
        getOrderByOrderNumber,
    } = useBetween(useApi);


    // Fetch and manage data (customer, order, delivery info)
    const {
        startDate,
        filteredDays,
        isLoadingCustomer,
        isLoadingOrder,
    } = usePickupData(
        customer,
        getDelivery,
        logout,
        setCartOpen
    );

    // Form state and handlers
    const {
        date,
        totalPrice,
        note,
        isSubmitting,
        remainingChars,
        setDate,
        setNote,
        handlePriceChange,
        handleSubmit,
        initializePaymentAmount,
        MAX_NOTE_LENGTH,
    } = usePickupForm(order, navigate);

    // Initialize payment amount when order loads
    useEffect(() => {
        if (order?.DueAmount?.value) {
            initializePaymentAmount(order);
        }
    }, [order, initializePaymentAmount]);

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            try {
                setUpdating(true);
                const response = await getOpenInvoiceByInvoiceId(invoiceId);

                if (!isMounted) return;

                const invoiceDetail = response.openInvoiceDetail;
                setInvoice(invoiceDetail);

                const hasExistingPickups = invoiceDetail.PaymentPickup && invoiceDetail.PaymentPickup.length > 0;
                setIsRescheduling(hasExistingPickups);

                const orderNumber = invoiceDetail.OrderNbr?.value;
                if (!orderNumber) {
                    setUpdating(false);
                    return;
                }

                const orderResponse = await getOrderByOrderNumber(orderNumber);
                if (isMounted) {
                    setOrder(orderResponse.order);
                    setUpdating(false);
                }
            } catch (error) {
                if (isMounted) {
                    setUpdating(false);
                    console.error('Data fetch error:', error);
                    toast.error('Sorry, we could not process this request. Please try again or contact your sales rep.');
                    navigate('/profile/open-invoices');
                }
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, [invoiceId]);

    // Show loading spinner while fetching initial data
    const isLoading = updating || isLoadingCustomer;

    return (
        <Layout>
            <div className='bg-gray'>
                <Container className='payment-pickup-container d-flex'>
                    <Navbar active='payment-pickup' />
                    <div className='payment-pickup-wrapper'>
                        <Row>
                            <Col sm={12} lg={12} md={12}>
                                <Form
                                    className='payment-pickup-summary'
                                    onSubmit={(e) => {
                                        const submitAction = isRescheduling
                                            ? (data) => reschedulePaymentPickup(invoice.PaymentPickup[invoice.PaymentPickup.length - 1].OrderNbr.value, data)
                                            : addPickUpPayment;

                                        handleSubmit(e, customerId, submitAction);
                                    }}
                                >
                                    {/* Customer Information */}
                                    <CustomerInfoCard customer={customer} />

                                    {/* Loading State */}
                                    {isLoading && (
                                        <div className='loading-wrapper text-center py-5'>
                                            <Spinner animation='border' role='status'>
                                                <span className='visually-hidden'>Loading...</span>
                                            </Spinner>
                                        </div>
                                    )}

                                    {/* Order Information and Pickup Form */}
                                    {!isLoading && Object.keys(order).length > 0 && (
                                        <>
                                            <OrderInfoCard order={order} />

                                            {invoice.PaymentPickup && invoice.PaymentPickup.length > 0 && (
                                                <>
                                                    <h4 className="mb-3">Existing Pickup Schedule</h4>
                                                    <div className="check-card mb-4 existing-pickups">
                                                        <div className="p-3">
                                                            {[...invoice.PaymentPickup].reverse().map((pickup, index) => {
                                                                const displayIndex = invoice.PaymentPickup.length - index;
                                                                const status = pickup.Status?.toLowerCase();
                                                                return (
                                                                    <div key={index} className="pickup-info-item p-3 mb-2 rounded bg-light border">
                                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                                            <span className="fw-bold">Pickup #{displayIndex}</span>
                                                                            <span className={`badge rounded-pill ${status === 'open' ? 'bg-primary' : 'bg-secondary'}`}>
                                                                                {pickup.Status || 'Scheduled'}
                                                                            </span>
                                                                        </div>
                                                                        <div className="row g-2">
                                                                            <div className="col-6">
                                                                                <small className="text-muted d-block font-12">Scheduled Date</small>
                                                                                <span className="font-13">{pickup.RequestedOn ? toDateType(pickup.RequestedOn) : 'N/A'}</span>
                                                                            </div>
                                                                            {pickup.PickUpAmount?.value && (
                                                                                <div className="col-6">
                                                                                    <small className="text-muted d-block font-12">Amount</small>
                                                                                    <span className="font-13">${toTwoDecimals(pickup.PickUpAmount.value)}</span>
                                                                                </div>
                                                                            )}
                                                                            {pickup.note && (
                                                                                <div className="col-12 mt-2">
                                                                                    <small className="text-muted d-block font-12">Notes</small>
                                                                                    <p className="mb-0 font-13">{pickup.note}</p>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}

                                                            {
                                                                isRescheduling
                                                                    ? <div className="mt-3 p-2 bg-warning-subtle rounded border border-warning">
                                                                        <small className="text-warning-emphasis">
                                                                            <strong>Note:</strong> Updating the request will update the schedule.
                                                                            Please coordinate with your sales rep if you need to cancel a pickup.
                                                                        </small>
                                                                    </div>
                                                                    : null
                                                            }
                                                        </div>
                                                    </div>
                                                </>
                                            )}

                                            <PickupScheduleForm
                                                date={date}
                                                setDate={setDate}
                                                startDate={startDate}
                                                filteredDays={filteredDays}
                                                totalPrice={totalPrice}
                                                handlePriceChange={handlePriceChange}
                                                note={note}
                                                setNote={setNote}
                                                remainingChars={remainingChars}
                                                maxNoteLength={MAX_NOTE_LENGTH}
                                                order={order}
                                                isRescheduling={isRescheduling}
                                            />

                                            {/* Submit Button */}
                                            <div className='check-card mb-4'>
                                                <div className='submit-group d-flex justify-content-center'>
                                                    <AwareButton
                                                        type='submit'
                                                        className='btn btn-primary'
                                                        disabled={isSubmitting}
                                                    >
                                                        {isSubmitting
                                                            ? 'Submitting...'
                                                            : 'Submit Pickup Request'}
                                                    </AwareButton>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {/* Contact Representative */}
                                    <ContactRepCard customer={customer} />
                                </Form>
                            </Col>
                        </Row>
                    </div>
                </Container>
            </div>
        </Layout>
    );
};

export default PaymentPickup;